import {
    OAUTH_COMPLETION_RETRY_CODES,
    OAUTH_PENDING_STORAGE_PREFIX,
    OAUTH_PROVIDERS,
    OAUTH_RETURN_FRAGMENT,
    OAUTH_RETURN_PROVIDER_PARAM,
    OAUTH_STATE_MAX_LENGTH,
    TIKTOKSHOP_SCOPE_NAMES,
} from '../constants/oauthProviders';
import { FormatDateTimeBr } from './date';
import { FormatDurationHuman } from './format';

/**
 * A TELA DE CONEXÕES (Health → Conexões) — utilitários PUROS: o que cada linha do cartão diz sobre a conexão de um
 * provedor, lido do que `GET /integrations/oauth/status` devolve. Nada aqui decide regra: o que o token permite é do
 * servidor; aqui só se escolhe o texto.
 *
 * 09/10 (pedido do dono: texto mínimo na tela, detalhe em modal): o SELO e a CONTA são curtos e iguais para os quatro
 * — a linha mostra só os dois —; a validade e os avisos (`notes`) vão para o modal da conexão.
 *
 * Mercado Livre e Bling (`tokenRenewal: 'automatic'`): o token é renovado por um worker. O TikTok Shop
 * (`on_use`) tem outra leitura: não há worker, então token de acesso vencido com a autorização ainda válida não é
 * defeito — ele renova no próximo uso —, e o que mata a conexão é a AUTORIZAÇÃO vencer, ou o token guardado não ser
 * deste ambiente (`token.usable_here` / `token.blocked_reason`).
 *
 * A Shopee (08/10) também é `on_use`, com leitura própria (`describeShopeeConnection`): `usable_here` e
 * `blocked_reason` vêm no nível do BLOCO, a validade da renovação é estimada (30 dias), e os detalhes dizem sempre o
 * ambiente (teste ou, desde 10/10, produção — a linha mostra só a palavra) e se a Redirect URL configurada volta para
 * este melidash.
 *
 * O RETORNO AUTOMÁTICO (TikTok Shop e Shopee, `returnMode: 'automatic'`): o callback do backend devolve o navegador a
 * `/health?tab=sistema&oauth_provider=<provedor>#oauth_return=<query codificada>`. `ReadOAuthReturn` lê isso,
 * `StripOAuthReturn` dá a barra limpa, e `ResolveOAuthReturn` aplica as três regras que impedem um endereço FORJADO
 * de fazer o melidash guardar o token da loja de outra pessoa (a crítica da especificação, seção 3.1; a regra 2 passou
 * a EXIGIR o `state` no retorno com o achado ST-01 da revisão).
 */

const CONNECT_AGAIN = 'Conecte de novo.';

/** O erro do `complete` quando a URL de retorno não tem `code` ou não tem `state` (`details.tem_code`, `tem_state`). */
const INCOMPLETE_CALLBACK_CODE = 'INT26_OAUTH_CALLBACK_URL_INCOMPLETE';
/**
 * Na VOLTA AUTOMÁTICA ninguém colou nada — a frase do servidor ("A URL colada não tem…") não serve. A volta só é
 * enviada com `state` (`ResolveOAuthReturn`), então o que falta é o código: o vendedor recusou, ou desistiu.
 */
const RETURN_WITHOUT_CODE_TEXT = 'A autorização voltou sem o código. Clique em Conectar para autorizar de novo.';
/**
 * No COLAR, a URL tem código e não tem `state`, e a tela não tinha nenhum guardado para mandar junto: o provedor não
 * devolveu o `state` (não está confirmado que a Shopee devolve) e o melidash foi aberto numa aba que não é a do
 * Conectar. O caminho que funciona daqui é autorizar de novo pelo botão do modal: o `state` novo fica nesta página.
 */
const PASTE_WITHOUT_STATE_HINT =
    'Esta URL não traz a marca do Conectar (`state`) e esta aba não guardou nenhuma: clique em "Autorizar em outra aba", autorize de novo e cole aqui a URL em que a aba nova parar.';

/**
 * O SELO da linha é curto e igual para os quatro provedores (09/10, pedido do dono: "texto mínimo na tela; detalhe em
 * modal/accordion"): `conectado`, `desconectado`, `expirado`, `bloqueado`, `sem token`, `sem credenciais`. O porquê
 * de um token `bloqueado` vai para os detalhes da conexão (o modal), nunca para a linha.
 */
const CHIP_EXPIRED = Object.freeze({ tone: 'warn', label: 'expirado' });
const CHIP_BLOCKED_LABEL = 'bloqueado';

/**
 * Por que o token guardado (TikTok Shop ou Shopee) não serve AQUI — o tom do selo e a frase de cada motivo que o
 * servidor informa. `foreign_api_stamp` e `no_shop` são só da Shopee. A autorização vencida é `expirado`; os outros
 * motivos são `bloqueado`.
 */
const BLOCKED_TOKEN_TEXTS = Object.freeze({
    placeholder_token: Object.freeze({
        tone: 'warn',
        note: `O token guardado é o enfeite que os scripts de cópia de banco gravam (DUMMY), não um token de verdade. ${CONNECT_AGAIN}`,
    }),
    no_stamp: Object.freeze({
        tone: 'warn',
        note: `O token guardado não tem o carimbo de onde foi autorizado e, por segurança, não é usado. ${CONNECT_AGAIN}`,
    }),
    foreign_stamp: Object.freeze({
        tone: 'warn',
        note: `O token guardado foi autorizado em outro ambiente ou em outro banco e não é usado nem renovado aqui. ${CONNECT_AGAIN}`,
    }),
    foreign_api_stamp: Object.freeze({
        tone: 'warn',
        note: `O token guardado foi autorizado com outro aplicativo ou em outro ambiente da Shopee e não é usado nem renovado aqui. ${CONNECT_AGAIN}`,
    }),
    no_shop: Object.freeze({
        tone: 'warn',
        note: `O token guardado não diz de qual loja é e, por segurança, não é usado. ${CONNECT_AGAIN}`,
    }),
    authorization_expired: Object.freeze({
        tone: 'bad',
        note: `A autorização da loja venceu: o token não renova mais. ${CONNECT_AGAIN}`,
    }),
});

/**
 * Os escopos quando o servidor não tem lista (`scopes.reason` de `GET /integrations/oauth/status`). Nenhuma chamada é
 * feita ao provedor para perguntar: o servidor lê só o que ficou guardado da troca do código e da última renovação.
 */
const SCOPES_NOT_REPORTED_TEXTS = Object.freeze({
    no_token: 'Sem autorização guardada.',
    not_reported: 'O provedor não informou.',
    app_level: 'Sem escopo por autorização: a permissão é do aplicativo.',
});

function describeScope(key) {
    return Object.hasOwn(TIKTOKSHOP_SCOPE_NAMES, key) ? `${TIKTOKSHOP_SCOPE_NAMES[key]} (${key})` : key;
}

function listOf(value) {
    return Array.isArray(value) ? value.filter((item) => typeof item === 'string' && item !== '') : [];
}

/** A linha como o cartão sempre a mostrou para o Mercado Livre e o Bling — o token é renovado por um worker. */
function describeAutomaticRenewal(status) {
    const token = status.token || {};
    const account = status.account;
    const connected = Boolean(token.has_access_token && !token.expired);

    let chip;
    if (!status.configured) {
        chip = { tone: 'bad', label: 'sem credenciais' };
    } else if (status.disconnected) {
        chip = { tone: 'off', label: 'desconectado' };
    } else if (connected) {
        chip = { tone: 'ok', label: 'conectado' };
    } else if (token.expired) {
        chip = CHIP_EXPIRED;
    } else {
        chip = { tone: 'off', label: 'sem token' };
    }

    let accountLine = '—';
    if (account?.reachable) {
        accountLine = `${account.nickname || 'sem apelido'} · id ${account.id}`;
    } else if (account) {
        accountLine = `conta inacessível (${account.reason})`;
    } else if (status.disconnected) {
        accountLine = 'nenhuma conta conectada';
    }

    // Token vencido diz "venceu em" (09/10): "expira … (-19880 min)" era a conta de cabeça que a tela devia fazer.
    let expiry = 'sem validade registrada';
    if (token.expires_at && token.expired === true) {
        expiry = `token venceu em ${FormatDateTimeBr(token.expires_at)}`;
    } else if (token.expires_at) {
        expiry = `token expira ${FormatDateTimeBr(token.expires_at)}${
            Number.isFinite(token.expires_in_minutes) ? ` (${token.expires_in_minutes} min)` : ''
        }`;
    }
    const renewal = token.has_refresh_token ? ' · renova sozinho' : ' · NÃO renova sozinho';

    return { connected, chip, account: accountLine, validity: `${expiry}${renewal}`, notes: [] };
}

/** A conta do TikTok Shop numa linha curta: a loja (ou, antes da primeira leitura, o vendedor) e a região. */
function describeStoredAccount(status) {
    if (status.disconnected) {
        return 'nenhuma conta conectada';
    }
    const stored = status.stored_account;
    if (!stored) {
        return '—';
    }
    const who = stored.shop_id
        ? stored.shop_name || `loja id ${stored.shop_id}`
        : stored.seller_name || 'vendedor sem nome';
    return [who, stored.region].filter(Boolean).join(' · ');
}

/** O que a linha curta da conta do TikTok Shop deixa de fora — vai para os detalhes. */
function describeStoredAccountNotes(status) {
    const stored = status.stored_account;
    if (status.disconnected || !stored) {
        return [];
    }
    if (!stored.shop_id) {
        return [{ tone: 'off', text: 'A loja aparece depois da primeira leitura do roteiro (lojas).' }];
    }
    const seller = stored.seller_name ? `vendedor ${stored.seller_name} · ` : '';
    return [{ tone: 'off', text: `${seller}loja id ${stored.shop_id}` }];
}

function describeAccessTokenValidity(token, connected) {
    if (!token.expires_at) {
        return 'token de acesso sem validade registrada';
    }
    const when = FormatDateTimeBr(token.expires_at);
    if (token.expired) {
        return `token de acesso venceu em ${when}${connected ? ' — renova no próximo uso' : ''}`;
    }
    const remaining = Number.isFinite(token.expires_in_minutes)
        ? ` (${FormatDurationHuman(token.expires_in_minutes)})`
        : '';
    return `token de acesso vale até ${when}${remaining}`;
}

function describeAuthorizationValidity(token) {
    if (!token.refresh_expires_at) {
        return 'autorização sem validade registrada';
    }
    const when = FormatDateTimeBr(token.refresh_expires_at);
    return token.refresh_expired ? `autorização venceu em ${when}` : `autorização vence em ${when}`;
}

/**
 * O selo de quem renova no uso (TikTok Shop e Shopee) e, quando o token não serve aqui, o aviso do motivo. Sem o
 * servidor dizer que o token serve AQUI (`usableHere === true`), nunca "conectado".
 */
function describeOnUseChip({ status, token, usableHere, blockedReason }) {
    const blocked = Object.hasOwn(BLOCKED_TOKEN_TEXTS, blockedReason) ? BLOCKED_TOKEN_TEXTS[blockedReason] : null;
    const connected = Boolean(token.present && !status.disconnected && usableHere === true);
    const notes = [];

    let chip;
    if (!status.configured) {
        chip = { tone: 'bad', label: 'sem credenciais' };
    } else if (status.disconnected) {
        chip = { tone: 'off', label: 'desconectado' };
    } else if (!token.present) {
        chip = { tone: 'off', label: 'sem token' };
    } else if (connected) {
        // Token de acesso vencido com a autorização valendo continua `conectado`: renova no próximo uso (a validade,
        // nos detalhes, diz isso).
        chip = { tone: 'ok', label: 'conectado' };
    } else if (blockedReason === 'authorization_expired') {
        chip = { tone: blocked.tone, label: CHIP_EXPIRED.label };
        notes.push({ tone: blocked.tone, text: blocked.note });
    } else if (blocked) {
        chip = { tone: blocked.tone, label: CHIP_BLOCKED_LABEL };
        notes.push({ tone: blocked.tone, text: blocked.note });
    } else {
        // O servidor disse que o token não serve aqui e não disse (ou disse num motivo que esta tela não conhece)
        // por quê: nunca "conectado".
        chip = { tone: 'warn', label: CHIP_BLOCKED_LABEL };
        notes.push({ tone: 'warn', text: `O token guardado não é usado neste ambiente. ${CONNECT_AGAIN}` });
    }
    return { connected, chip, notes };
}

/** A linha do TikTok Shop — ninguém renova o token sozinho; quem renova é quem usa, na hora de usar. */
function describeRenewalOnUse(provider, status) {
    const token = status.token || {};
    const { connected, chip, notes } = describeOnUseChip({
        status,
        token,
        usableHere: token.usable_here,
        blockedReason: token.blocked_reason,
    });

    const tokenStored = Boolean(token.present && !status.disconnected);
    const validity = tokenStored
        ? `${describeAccessTokenValidity(token, connected)} · ${describeAuthorizationValidity(token)}`
        : 'sem validade registrada';
    notes.push(...describeStoredAccountNotes(status));

    // Escopo faltando ou sobrando só diz alguma coisa de uma autorização que está valendo: sem token guardado, a
    // lista de concedidos vem vazia e "faltam os quatro" seria ruído.
    if (connected) {
        const missing = listOf(status.missing_scopes);
        const extra = listOf(status.extra_scopes);
        if (missing.length > 0) {
            notes.push({
                tone: 'warn',
                text: `Falta escopo: ${missing.map(describeScope).join(', ')}. As leituras que dependem dele serão recusadas — ligue-o no aplicativo (Partner Center → App & Service → Manage API) e conecte de novo.`,
            });
        }
        if (extra.length > 0) {
            notes.push({
                tone: 'warn',
                text: `Escopo a mais: ${extra.map(describeScope).join(', ')}. A autorização dá ao aplicativo mais do que as leituras pedem; o combinado são só os quatro.`,
            });
        }
    }
    if (provider.rowNote) {
        notes.push({ tone: 'off', text: provider.rowNote });
    }

    return { connected, chip, account: describeStoredAccount(status), validity, notes };
}

function describeShopeeStoredShop(status) {
    if (status.disconnected) {
        return 'nenhuma conta conectada';
    }
    const stored = status.stored_account;
    if (!stored?.shop_id) {
        return '—';
    }
    if (!stored.shop_name) {
        return `loja id ${stored.shop_id}`;
    }
    return [stored.shop_name, stored.region].filter(Boolean).join(' · ');
}

/**
 * O ambiente da Shopee, nos detalhes: teste (sandbox) ou, desde 10/10, produção (live) — a loja oficial, só leitura. A
 * linha mostra só a palavra (`DescribeOAuthEnvironment`).
 */
function describeShopeeEnvironment(apiEnv) {
    if (apiEnv === 'sandbox') {
        return { tone: 'off', text: 'Ambiente da Shopee: teste (sandbox) — só loja de teste.' };
    }
    if (apiEnv === 'live') {
        return { tone: 'info', text: 'Ambiente da Shopee: produção (live) — a loja oficial, só leitura.' };
    }
    return {
        tone: 'warn',
        text: 'Ambiente da Shopee: não reconhecido (SHOPEE_API_ENV) — nada é autorizado nem lido.',
    };
}

/**
 * A linha da Shopee. O token de acesso vale 4 horas e renova no próximo uso; o de renovação vale 30 dias a partir da
 * última renovação (a Shopee não informa: é estimativa do servidor); a autorização da LOJA vem da primeira leitura.
 */
function describeShopeeConnection(provider, status) {
    const token = status.token || {};
    const { connected, chip, notes } = describeOnUseChip({
        status,
        token,
        usableHere: status.usable_here,
        blockedReason: status.blocked_reason,
    });

    let validity = 'sem validade registrada';
    if (token.present && !status.disconnected) {
        const parts = [describeAccessTokenValidity(token, connected)];
        if (token.refresh_expires_at_estimated) {
            const when = FormatDateTimeBr(token.refresh_expires_at_estimated);
            parts.push(
                token.refresh_expired ? `autorização estimada venceu em ${when}` : `autorização estimada até ${when}`,
            );
        } else {
            parts.push('autorização sem validade estimada');
        }
        if (status.stored_account?.authorization_expires_at) {
            parts.push(`loja autorizada até ${FormatDateTimeBr(status.stored_account.authorization_expires_at)}`);
        }
        validity = parts.join(' · ');
    }

    const stored = status.stored_account;
    if (!status.disconnected && stored?.shop_id) {
        notes.push({
            tone: 'off',
            text: stored.shop_name
                ? `loja id ${stored.shop_id}`
                : 'O nome da loja aparece depois da primeira leitura do roteiro (loja).',
        });
    }
    notes.push(describeShopeeEnvironment(status.api_env));
    notes.push(
        status.returns_here === true
            ? {
                  tone: 'off',
                  text: 'Retorno automático: depois de autorizar, o navegador volta sozinho para esta tela.',
              }
            : {
                  tone: 'warn',
                  text: 'Retorno por colagem — a Redirect URL configurada (SHOPEE_REDIRECT_URL) não é a deste melidash: depois de autorizar, use "Colar URL de retorno".',
              },
    );
    if (provider.rowNote) {
        notes.push({ tone: 'off', text: provider.rowNote });
    }

    return { connected, chip, account: describeShopeeStoredShop(status), validity, notes };
}

/**
 * O que a linha de um provedor mostra: se aparece, se está conectado (decide "Conectar" × "Reautenticar"), o selo, a
 * linha da conta, a linha da validade e os avisos. Nunca traz token — o status do servidor também não.
 * @param {object} provider uma entrada de `OAUTH_PROVIDERS`
 * @param {object} [status] o bloco deste provedor em `GET /integrations/oauth/status` (`providers[key]`)
 * @returns {{ visible: boolean, connected: boolean, chip: { tone: string, label: string }, account: string,
 *   validity: string, notes: Array<{ tone: string, text: string }> }}
 */
export function DescribeOAuthConnection(provider, status) {
    const block = status || {};
    let description;
    if (provider.key === 'shopee') {
        description = describeShopeeConnection(provider, block);
    } else if (provider.tokenRenewal === 'on_use') {
        description = describeRenewalOnUse(provider, block);
    } else {
        description = describeAutomaticRenewal(block);
    }
    return { visible: !(provider.hideWhenUnconfigured && !block.configured), ...description };
}

/**
 * A palavra do AMBIENTE na linha do provedor — "teste" ou "produção" —, sem descrição (regra do dono: tela enxuta).
 * Só para quem declara `environmentTags` (a Shopee) e só para um `api_env` que o servidor reconhece; senão, nada.
 * @returns {{ apiEnv: string, label: string, tone: 'warn' | 'off' } | null}
 */
export function DescribeOAuthEnvironment(provider, status) {
    const apiEnv = status?.api_env;
    const tags = provider?.environmentTags;
    if (!tags || typeof apiEnv !== 'string' || !Object.hasOwn(tags, apiEnv)) {
        return null;
    }
    return { apiEnv, label: tags[apiEnv], tone: apiEnv === 'live' ? 'warn' : 'off' };
}

/**
 * O provedor com o que muda no AMBIENTE que o status informa (`apiEnvOverrides[api_env]`): os passos do modal de
 * colar e o campo do console onde vai o domínio. Sem ambiente reconhecido, o provedor como está (os textos do teste).
 */
export function ResolveOAuthProviderForStatus(provider, status) {
    const overrides = provider?.apiEnvOverrides;
    const apiEnv = status?.api_env;
    if (!overrides || typeof apiEnv !== 'string' || !Object.hasOwn(overrides, apiEnv)) {
        return provider;
    }
    return { ...provider, ...overrides[apiEnv] };
}

/**
 * Os ESCOPOS que a autorização concedeu, para o accordion "Escopos" do modal da conexão — o mesmo para os quatro
 * provedores. Vêm SÓ de `scopes` do status (o servidor lê o que ficou guardado: a troca do código e, no Mercado Livre
 * e no Bling, a última renovação). Sem lista, a frase do motivo; nunca uma chamada ao provedor. No TikTok Shop o nome
 * do Partner Center vem junto da chave, e `missing` traz o que as leituras pedem e a autorização não deu.
 * @param {object} provider uma entrada de `OAUTH_PROVIDERS`
 * @param {object} [status] o bloco deste provedor em `GET /integrations/oauth/status`
 * @returns {{ reported: boolean, items: string[], missing: string[], text: string | null }}
 */
export function DescribeOAuthScopes(provider, status) {
    const scopes = status?.scopes;
    const granted = listOf(scopes?.granted);
    if (scopes?.reported !== true || granted.length === 0) {
        const reason = Object.hasOwn(SCOPES_NOT_REPORTED_TEXTS, scopes?.reason) ? scopes.reason : 'not_reported';
        return { reported: false, items: [], missing: [], text: SCOPES_NOT_REPORTED_TEXTS[reason] };
    }
    const missing = provider.key === 'tiktokshop' ? listOf(status.missing_scopes) : [];
    return { reported: true, items: granted.map(describeScope), missing: missing.map(describeScope), text: null };
}

/**
 * O corpo do `POST /integrations/oauth/:provider/start`. Só quem usa `redirect_uri` o manda: para o TikTok Shop o
 * corpo é `{}` (a rota valida `redirect_uri` como URL, e o link dele não leva endereço de retorno).
 */
export function BuildOAuthStartBody(provider, redirectUri) {
    return provider.sendsRedirectUri ? { redirect_uri: redirectUri } : {};
}

/**
 * A frase de erro do modal quando a conclusão falha. Para o Mercado Livre e o Bling é a mensagem do servidor, como
 * sempre foi. Para o TikTok Shop e a Shopee soma o que o provedor respondeu (o código do TikTok ou o nome do erro da
 * Shopee, a mensagem e o `request_id` — o servidor nunca devolve o corpo da resposta), os NOMES das variáveis que
 * faltam ou estão inválidas e, quando só uma autorização nova resolve, diz isso: o código da URL vale uma vez.
 *
 * A URL de retorno INCOMPLETA (TikTok Shop e Shopee) tem frase própria: na volta automática
 * (`automaticReturn: true` — a linha do provedor, sem ninguém ter colado nada) é "voltou sem o código"; no colar, com
 * código e sem `state`, soma o que fazer.
 * @param {object} provider uma entrada de `OAUTH_PROVIDERS`
 * @param {{ message?: string, code?: string, details?: object } | null} error o erro do `complete`
 * @param {{ automaticReturn?: boolean }} [context] quem mostra a frase: a volta automática, ou (padrão) o modal de colar
 */
export function DescribeOAuthCompletionError(provider, error, { automaticReturn = false } = {}) {
    const message = error?.message || 'Não consegui concluir a autorização.';
    if (provider.tokenRenewal !== 'on_use') {
        return message;
    }
    const details = error?.details || {};
    if (error?.code === INCOMPLETE_CALLBACK_CODE) {
        if (automaticReturn) {
            return RETURN_WITHOUT_CODE_TEXT;
        }
        return details.tem_code === true && details.tem_state === false
            ? `${message} ${PASTE_WITHOUT_STATE_HINT}`
            : message;
    }
    const parts = [message];
    const answered = [
        details.provider_code !== null && details.provider_code !== undefined
            ? `código ${details.provider_code}`
            : null,
        details.provider_error ? `erro ${details.provider_error}` : null,
        details.provider_message || null,
    ].filter(Boolean);
    if (answered.length > 0) {
        const requestId = details.request_id ? ` (request_id ${details.request_id})` : '';
        const who = provider.ofLabel?.startsWith('da ') ? `A ${provider.shortName}` : `O ${provider.shortName}`;
        parts.push(`${who} respondeu: ${answered.join(' — ')}${requestId}.`);
    }
    if (Array.isArray(details.missing) && details.missing.length > 0) {
        parts.push(`Falta no ambiente: ${details.missing.join(', ')}.`);
    }
    if (Array.isArray(details.invalid) && details.invalid.length > 0) {
        parts.push(`Valor inválido no ambiente: ${details.invalid.join(', ')}.`);
    }
    if (provider.completionRetryHint && OAUTH_COMPLETION_RETRY_CODES.includes(error?.code)) {
        parts.push(provider.completionRetryHint);
    }
    return parts.join(' ');
}

/** O tamanho máximo da query que o callback do backend repassa (`routes/integrations.js`): acima disso não é dele. */
const RETURN_QUERY_MAX_LENGTH = 2048;

function isFilledText(value) {
    return typeof value === 'string' && value !== '';
}

/**
 * O que o callback do backend deixou na barra: `?…&oauth_provider=<provedor>#oauth_return=<query codificada>`.
 * Só aceita provedor de `returnMode: 'automatic'`; fragmento malformado, vazio ou grande demais é "não há retorno".
 * A query sai decodificada UMA vez (o que está codificado dentro dela quem decodifica é o servidor).
 * @param {{ search?: string, hash?: string }} location
 * @returns {{ providerKey: string, callbackQuery: string } | null}
 */
export function ReadOAuthReturn(location) {
    const search = typeof location?.search === 'string' ? location.search : '';
    const hash = typeof location?.hash === 'string' ? location.hash : '';
    if (!hash.startsWith(OAUTH_RETURN_FRAGMENT)) {
        return null;
    }
    const providerKey = new URLSearchParams(search).get(OAUTH_RETURN_PROVIDER_PARAM);
    const provider = OAUTH_PROVIDERS.find((entry) => entry.key === providerKey && entry.returnMode === 'automatic');
    if (!provider) {
        return null;
    }
    let callbackQuery;
    try {
        callbackQuery = decodeURIComponent(hash.slice(OAUTH_RETURN_FRAGMENT.length));
    } catch {
        return null;
    }
    if (callbackQuery === '' || callbackQuery.length > RETURN_QUERY_MAX_LENGTH) {
        return null;
    }
    return { providerKey: provider.key, callbackQuery };
}

/**
 * A barra limpa depois de lido o retorno: o mesmo caminho e a mesma query (a aba e o resto, na ordem), sem o
 * `oauth_provider` e SEM fragmento — o código não fica na barra nem no histórico.
 * @param {{ pathname?: string, search?: string }} location
 */
export function StripOAuthReturn(location) {
    const pathname = isFilledText(location?.pathname) ? location.pathname : '/';
    const params = new URLSearchParams(typeof location?.search === 'string' ? location.search : '');
    params.delete(OAUTH_RETURN_PROVIDER_PARAM);
    const query = params.toString();
    return query ? `${pathname}?${query}` : pathname;
}

/** O `state` que uma URL de retorno traz — inteira, só a query, ou a URL do front já repassada (no fragmento). */
function readStateFromCallback(callbackUrl) {
    const text = String(callbackUrl ?? '');
    let query;
    const relayed = text.indexOf(OAUTH_RETURN_FRAGMENT);
    if (relayed >= 0) {
        try {
            query = decodeURIComponent(text.slice(relayed + OAUTH_RETURN_FRAGMENT.length));
        } catch {
            return null;
        }
    } else {
        const withoutHash = text.split('#')[0];
        const mark = withoutHash.indexOf('?');
        query = mark >= 0 ? withoutHash.slice(mark + 1) : withoutHash;
    }
    const state = new URLSearchParams(query).get('state');
    return isFilledText(state) ? state : null;
}

/**
 * O corpo do `POST /integrations/oauth/:provider/complete` no COLAR: a URL de retorno e — SÓ quando ela não traz
 * `state` (não está confirmado que a Shopee o devolve) — o `state` que esta tela recebeu do `start`. No servidor, o da
 * URL vence, e o do corpo só é aceito para a Shopee. A volta automática NÃO usa isto: ela exige o `state` no retorno.
 * @param {string} callbackUrl a URL colada, ou `?` + a query repassada
 * @param {string | null} [pendingState] o `state` do Conectar
 */
export function BuildOAuthCompleteBody(callbackUrl, pendingState) {
    const body = { callback_url: callbackUrl };
    const usable = isFilledText(pendingState) && pendingState.length <= OAUTH_STATE_MAX_LENGTH;
    if (usable && readStateFromCallback(callbackUrl) === null) {
        body.state = pendingState;
    }
    return body;
}

/**
 * AS TRÊS REGRAS DA VOLTA AUTOMÁTICA. Um terceiro consegue um código do aplicativo do dono (o link de autorização só
 * leva o `partner_id`, que não é segredo) e monta `…/health?…&oauth_provider=shopee#oauth_return=code%3D…`; se a tela
 * concluísse o que estiver no fragmento, o melidash guardaria o token da loja DELE. Por isso:
 *  1. só conclui se houver pendência DESTA ABA (`pendingState`, tirado do `sessionStorage`);
 *  2. o retorno TEM de trazer o `state`, IDÊNTICO ao guardado;
 *  3. a pendência é apagada antes do `complete` — quem chama já a tirou (`TakePendingOAuthState`).
 *
 * A regra 2 EXIGE o `state` (achado ST-01 da revisão; antes era "se o retorno traz `state`…"): o callback do backend
 * só repassa o navegador quando a query traz um `state` válido — sem ele, mostra a página de plano B. Um fragmento
 * sem `state` nunca veio do backend: foi digitado ou forjado, e concluí-lo com o `state` guardado seria exatamente o
 * ataque de cima, na aba em que há um Conectar pendente. O `state` guardado só vai no corpo no COLAR
 * (`BuildOAuthCompleteBody`, pelo modal), que é a pessoa colando de propósito a URL que acabou de ver.
 * @returns {{ send: boolean, reason: 'no_pending' | 'state_mismatch' | null, body: object | null }}
 */
export function ResolveOAuthReturn({ callbackQuery, pendingState }) {
    if (!isFilledText(pendingState)) {
        return { send: false, reason: 'no_pending', body: null };
    }
    const callbackUrl = `?${callbackQuery}`;
    if (readStateFromCallback(callbackUrl) !== pendingState) {
        return { send: false, reason: 'state_mismatch', body: null };
    }
    return { send: true, reason: null, body: { callback_url: callbackUrl } };
}

/**
 * Guarda o `state` do Conectar nesta aba, ANTES de navegar para a autorização. `sessionStorage` pode não existir ou
 * lançar (janela privada): devolve `false`, e quem chama cai no plano B (aba nova e colar).
 */
export function SavePendingOAuthState(storage, providerKey, state) {
    if (!storage || !isFilledText(state)) {
        return false;
    }
    try {
        storage.setItem(`${OAUTH_PENDING_STORAGE_PREFIX}${providerKey}`, state);
        return true;
    } catch {
        return false;
    }
}

/**
 * Lê E APAGA a pendência desta aba (uma tentativa por Conectar). Se não conseguir apagar, não devolve: uma pendência
 * que continuaria valendo depois de usada não serve.
 */
export function TakePendingOAuthState(storage, providerKey) {
    if (!storage) {
        return null;
    }
    try {
        const key = `${OAUTH_PENDING_STORAGE_PREFIX}${providerKey}`;
        const state = storage.getItem(key);
        storage.removeItem(key);
        return isFilledText(state) ? state : null;
    } catch {
        return null;
    }
}

/**
 * A URL EXATA a cadastrar no aplicativo do provedor para a autorização voltar sozinha: o callback DESTA instância
 * (`callback_url` do status) e onde ela vai. Na Shopee a URL mora na variável e o console leva só o domínio. Só para
 * provedor de retorno automático; sem `callback_url` http(s) válida, nada.
 * @returns {{ url: string, where: string, domainNote: string | null } | null}
 */
export function DescribeOAuthRegistration(provider, status) {
    if (provider.returnMode !== 'automatic' || !isFilledText(status?.callback_url)) {
        return null;
    }
    let parsed;
    try {
        parsed = new URL(status.callback_url);
    } catch {
        return null;
    }
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
        return null;
    }
    return {
        url: status.callback_url,
        where: provider.registerWhere,
        domainNote: provider.registerDomainOnly ? `${provider.registerDomainOnly}: ${parsed.hostname}` : null,
    };
}
